import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';

import {
  CustomChipStatus,
  GroupAction,
  ContainerGroup,
  IconArea,
  CustomTyphography,
  CustomTableContainer,
} from './styles';

import { ColorsContext } from "../../../../Context/ColorsContext"
import { Cell,
	HeaderCell,
	CustomTableHead,
	TableContainer,
	TableContent, 
  CustomIconButtonDemo,
  CustomTable,
} from '../../../../pages/styles';
import { LoadingProgress } from '../../../LoadingProgress';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';

// Utils
import { useTranslation } from 'react-i18next';
import { MediaQuerys } from '../../../../styles/GlobalStyle';
import { returnStringContract } from '../../../../utils/commonFunctions';
import { FormatDate } from '../../../../utils/formatDate';
import { useSortByColumn } from '../../../../Hooks/useSortByColumn';
import { GroupedChipsWithTooltip } from '../../GroupedChipsWithTooltip';

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../Context/PermissionsContext';

const headCells = [
  {
    id: 'email',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.email',
    sticky: true,
    direction: 'left',
  },
  {
    id: 'name',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.fullname',
  },
  {
    id: 'identification',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.identification',
  },
  {
    id: 'role',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.role',
    customSort: "role?.name",
  },
  {
    id: 'createAt',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.createAt',
    customSort: "createdAt",
  },
  {
    id: 'lastLogin',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.lastLogin',
    customSort: "lastLogin",
  },
  {
    id: 'company',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.companyUser',
    customSort: "company",
  },
  {
    id: 'contract',
    center: false,
    disablePadding: true,
    sortArrow: false,
    label: 'system.users.table.contract',
  },
  {
    id: 'plant',
    center: false,
    disablePadding: true,
    sortArrow: false,
    label: 'system.users.table.plants',
  },
  {
    id: 'authenticationType',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.authentication',
  },
  {
    id: 'isEnabled',
    center: false,
    disablePadding: true,
    sortArrow: true,
    label: 'system.users.table.status',
    customSort: "isEnabled ? 'Activo' : 'Inactivo'"
  },
  {
    id: 'actions',
    center: true,
    disablePadding: true,
    sortArrow: false,
    label: 'common.actions.title',
    sticky: true,
    direction: 'right',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, pageRef, loadData } = props;
	const { t } = useTranslation();

  const createSortHandler = (property, customSort) => (event) => {
    let newOrder = 'asc'; // Valor inicial del orden (primer clic debería ser ascendente)

    if (orderBy === property && order === 'asc') {
      newOrder = 'desc'; // Si ya es ascendente, cambiar a descendente
    } else if (orderBy === property && order === 'desc') {
      newOrder = 'asc'; // Si ya es descendente, cambiar a ascendente
    }

    pageRef.current.order = property;
    pageRef.current.sort = newOrder.toUpperCase();
    onRequestSort(event, property, customSort);
    loadData();
  };

  return (
    <CustomTableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <HeaderCell
            key={headCell.id}
            align={headCell.center ? 'center' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            dataHead={headCell}
          >
            {headCell.sortArrow == true &&
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id, headCell.customSort)}
              >
                {t(headCell.label) || headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span">
                  </Box>
                ) : null}
              </TableSortLabel>
            }
            {headCell.sortArrow == false &&
              <>
                <Box component="span">
                  {t(headCell.label) || headCell.label}
                </Box>
              </>
            }
          </HeaderCell>
        ))}
      </TableRow>
    </CustomTableHead >
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function TableUsers({
  filteredRequest,
  rows,
  selectedUser,
  addNewRole,
  handleRemove,
  loadingData,
	currentPage,
  rowsCount,
  loadData,
  onChangePage,
  pageRef,
}) {
  const { mobile } = MediaQuerys;
	const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const { colors } = React.useContext(ColorsContext);
  const ability = useAbility(AbilityContext);
  const { order, orderBy, stableSort, getComparator, handleRequestSort } = useSortByColumn({ defaultSort: 'lastLogin' })
  const {format} = new FormatDate();

  React.useEffect(() => {
    setPage(currentPage);
  }, [filteredRequest]);

  const handleChangePage = async(_, newPage) => {
    await onChangePage(newPage, rowsPerPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async(event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    await onChangePage(0, event.target.value, true);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: '100%', marginTop: '16px', [mobile]: { marginTop: '15px'} }}>
      <TableContainer sx={{ width: '100%', mb: 2 }}>
        <CustomTableContainer>
          <CustomTable stickyHeader
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              loadData={loadData}
              pageRef={pageRef}
            />
            {loadingData && 
                <TableRow style={{ height: 33 * emptyRows, margin: '0 auto'}}>
                    <TableCell colSpan={12}>
                        <LoadingProgress/>                                        
                    </TableCell>
                </TableRow>
            }
						{!loadingData && 
              <TableContent>
                  {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                    rows.sort(getComparator(order, orderBy)).slice() */}
                  {stableSort(filteredRequest, getComparator())
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={row.id}
                        >
                          <Cell direction="left">{row.email}</Cell>
                          <Cell sx={{ border: "none" }}>{`${row.name ?? ""} ${row.lastName ?? ""}`}</Cell>
                          <Cell sx={{ border: "none" }}>{row.identification}</Cell>
                          <Cell sx={{ border: "none" }}>{row.role?.name}</Cell>
                          <Cell sx={{ border: "none" }}>{format({date: row?.createdAt, format: true})}</Cell>
                          <Cell sx={{ border: "none" }}>{format({ date: row?.lastLogin, format: true })}</Cell>
                          <Cell sx={{ border: "none" }}>{row?.company?.name}</Cell>
                          {row.contractPlants?.length > 0 ?
                              <>
                                  <Cell sx={{ border: "none" }}>
                                    {row.contractPlants[1] !== undefined ? (
                                        <Tooltip title={returnStringContract(row.contractPlants, true)}>
                                          <ContainerGroup>
                                            <div>
                                              <CustomTyphography>{row.contractPlants[0]?.contract?.code || ''}</CustomTyphography>
                                            </div>
                                            <div>
                                              <IconArea>{`+${row.contractPlants.length - 1}`}</IconArea>
                                            </div>
                                          </ContainerGroup>
                                        </Tooltip>
                                      ) : (
                                        <span>{returnStringContract(row.contractPlants)}</span>
                                      )
                                    }
                                  </Cell>
                                  <Cell sx={{ border: "none" }}>
                                    {(row.contractPlants?.flatMap(cp => cp.plants || []) || []).length > 0 ? (
                                      <GroupedChipsWithTooltip 
                                        data={row.contractPlants.flatMap(cp => cp.plants || [])}
                                        getPropsObj={(el) => ({ label: el.name })}
                                      />
                                    ) : (
                                      <span></span>
                                    )}
                                  </Cell>
                              </>
                          :
                              <>
                                  <Cell sx={{ border: "none" }}>{row.contract?.code}</Cell>
                                  <Cell sx={{ border: "none" }}>
                                    {row.plants?.length > 0 ? (
                                      <GroupedChipsWithTooltip 
                                        data={row.plants}
                                        getPropsObj={(el) => ({ label: el.name })}
                                      />
                                    ) : (
                                      <GroupedChipsWithTooltip 
                                        data={row.plant ? [row.plant] : []}
                                        getPropsObj={(el) => ({ label: el?.name || '' })}
                                      />
                                    )}
                                  </Cell>
                              </>
                          }
                          <Cell sx={{ border: "none" }}>
                            {row.authenticationType === 'ACTIVE_DIRECTORY' ? t('system.users.table.actDir') : t('system.users.table.app')}
                          </Cell>
                          <Cell sx={{ border: "none" }}>
                            <CustomChipStatus 
                              enabled={row.isEnabled} 
                              icon={row.isEnabled ? <CheckCircleRoundedIcon /> : <CancelRoundedIcon />}
                              label={row.isEnabled ? t('system.users.table.active') : t('system.users.table.inactive')}
                            />
                          </Cell>
                          <Cell direction="right">
                            {row.isEnabled && (
                              <GroupAction>
                                {ability.can("UpdateUser") &&
                                  <Tooltip title={t('system.users.editUser')}>
                                    <CustomIconButtonDemo
                                      size="small"
                                      aria-label="detail"
                                      btnType="primary"
                                      onClick={() => selectedUser(row, 'UPDATE')}
                                    >
                                      <EditIcon />
                                    </CustomIconButtonDemo>
                                  </Tooltip>
                                }
                                
                                {ability.can("UpdateUser") &&
                                  <Tooltip title={t('system.users.table.duplicateUser')}>
                                    <CustomIconButtonDemo
                                      size="small"
                                      aria-label="detail"
                                      btnType="primary"
                                      onClick={() => addNewRole(row, 'ADD_ROLE')}
                                    >
                                      <ContentCopyIcon/>
                                    </CustomIconButtonDemo>
                                  </Tooltip>
                                }

                                {ability.can("ToggleStateUser") &&
                                  <Tooltip title={t('system.users.table.inactiveUser')}>
                                    <CustomIconButtonDemo
                                      size="small"
                                      aria-label="detail"
                                      btnType="delete"
                                      onClick={() => handleRemove(row, 'DEACTIVATE')}
                                    >
                                      <DeleteIcon />
                                    </CustomIconButtonDemo>
                                  </Tooltip>
                                }
                              </GroupAction>
                            )}
                            {!row.isEnabled && (
                              <>
                              {ability.can("ToggleStateUser") &&
                                <Tooltip title={t('system.users.table.activeUser')}>
                                  <CustomIconButtonDemo
                                      size="small"
                                      aria-label="detail"
                                      btnType="secondary"
                                      onClick={() => handleRemove(row, 'ACTIVATE')}
                                      sx={{
                                        width: "100%",
                                        borderRadius: "8px",
                                        padding: "4px 4px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "2px",
                                        lineHeight: 1,
                                        "& .MuiSvgIcon-root": {
                                          fontSize: "16px !important",
                                        }
                                      }}
                                    >
                                      <PersonOutlineRoundedIcon />
                                      <Box sx={{
                                        fontSize: "11px",
                                        display: { xs: "none", sm: "block" }
                                      }}>
                                        <Box component="span" sx={{ display: { xs: "none", lg: "flex" } }}>
                                          {t('system.users.table.activeUser')}
                                        </Box>
                                        <Box component="span" sx={{ display: { xs: "flex", lg: "none" } }}>
                                          {t('system.users.table.activate')}
                                        </Box>
                                      </Box>
                                    </CustomIconButtonDemo>
                                </Tooltip>
                              }
                              </>
                            )}
                          </Cell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: (dense ? 33 : 53) * emptyRows,
                      }}
                    >
                      <Cell colSpan={6} />
                    </TableRow>
                  )}
              </TableContent>
            }
          </CustomTable>
        </CustomTableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rowsCount}
          rowsPerPage={rowsPerPage}
          page={page}
          showFirstButton
          showLastButton
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common.itemsPerPage')}
          labelDisplayedRows={
            ({ from, to, count }) => {
              return '' + from + '-' + to + ` ${t('common.of')} ` + count
            }
          }
          sx={{
            borderTop: '1px solid ' + colors.gray,
          }}
        />
      </TableContainer>
    </Box>
  );
}
